import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller, Control, UseFormSetValue } from 'react-hook-form';
import {
  LinearProgress,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import * as Sentry from '@sentry/react';
import { push } from 'react-router-redux';

import BackButtonLayoutTitleAction from 'retired/BackButtonLayout/BackButtonLayoutTitleAction';
import { selectActiveDonationCreditRules } from 'entities/donationCreditRule/selectors';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { selectBudgets } from 'entities/budget/selectors';
import { showErrorAction } from 'containers/Notification/actions';
import { BudgetSubEntitySelector } from 'containers/CorporateDonationDonateFormPage/BudgetSubEntitySelector';
import { FilesUpload } from 'containers/FilesUpload/FilesUpload';
import {
  ReceiptType,
  useReceiptQuery,
  useCreateDonationCreditSeedReceiptMutation,
} from 'generated/graphql';

import { initAction } from './actions';
import epics from './epics';
import { KEY } from './constants';
import reducer from './reducers';
import { selectLoading } from './selectors';
import { RecentReceiptsList } from './RecentReceiptsList';

interface FormData {
  donationCreditRuleId: string;
  importedUrl: string;
  note: string;
  createUsers: boolean;
  notifyUser: boolean;
}

const DonationsSeedRuleSelector: React.FC<{
  control: Control<FormData>;
  setValue: UseFormSetValue<FormData>;
  selectedRuleId: string;
}> = ({ control, setValue, selectedRuleId }) => {
  const budgets = useSelector(selectBudgets);
  const donationCreditRules = useSelector(selectActiveDonationCreditRules);
  const relevantDonationCreditRules = donationCreditRules.filter(
    (rule) =>
      !rule?.type || ['DonationCreditRule', 'OneTimeDonationCreditRule'].includes(rule?.type)
  );
  useEffect(() => {
    if (!selectedRuleId && relevantDonationCreditRules?.size) {
      setValue('donationCreditRuleId', relevantDonationCreditRules.first().get('id') as string);
    }
  }, [selectedRuleId, relevantDonationCreditRules, setValue]);

  return (
    <>
      {relevantDonationCreditRules?.size ? (
        <Controller
          control={control}
          name="donationCreditRuleId"
          rules={{ required: 'Please select the donation credit rule' }}
          render={({ field: { onChange, value } }) => (
            <BudgetSubEntitySelector
              id="budget-rule-select"
              onChange={onChange}
              fullWidth
              label="Donation Credit Rule"
              // @NOTE-AC: @material-ui/Select – "Set to an empty string `''` if you don't want any of the available options to be selected"
              value={value || ''}
              budgets={budgets}
              subBudgets={relevantDonationCreditRules}
            />
          )}
        />
      ) : (
        <LinearProgress />
      )}
    </>
  );
};

const DonationsSeedPage: React.FC = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectLoading);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  useInjectReducer({ key: KEY, reducer });
  useInjectEpics({ key: KEY, epics });
  useEffect(() => {
    dispatch(initAction());
  }, [dispatch]);

  const {
    handleSubmit: reactHookFormHandleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormData>({ mode: 'onChange' });

  const donationCreditRuleId = watch('donationCreditRuleId');

  const {
    data: donationCreditReceiptData,
    error: donationCreditReceiptError,
    loading: donationCreditReceiptLoading,
    refetch: donationCreditReceiptRefetch,
  } = useReceiptQuery({
    variables: {
      where: {
        type: { equals: ReceiptType.DonationCreditSeed },
      },
    },
    skip: false,
  });

  const [
    createDonationCreditSeedReceiptMutation,
    { loading: loadingCreateDonationCreditSeedReceiptMutation, reset },
  ] = useCreateDonationCreditSeedReceiptMutation({
    onError: (error) => {
      Sentry.captureException(error);
      dispatch(showErrorAction('An error occurred while creating the donation credit seed run.'));
    },
  });

  const onSubmit = reactHookFormHandleSubmit(async (formData: FormData) => {
    await createDonationCreditSeedReceiptMutation({
      onCompleted: async (data) => {
        await donationCreditReceiptRefetch();
        dispatch(
          push(`/donations/credit-seeding/${data.mutationCreateDonationCreditSeedReceipt.id}`)
        );
      },
      onError: (error) => {
        dispatch(showErrorAction('There was an error processing this request'));
        console.error(error);
        reset();
      },
      variables: {
        donationCreditRuleId: formData.donationCreditRuleId,
        importedUrl: formData.importedUrl,
        createUsers: formData.createUsers,
        note: formData.note,
        notifyUser: formData.notifyUser,
      },
    });
  });

  if (donationCreditReceiptError) {
    dispatch(showErrorAction('An error occurred loading the data, please try again.'));
  }

  return (
    <>
      <Helmet title="Seed Donation Credits" />
      <BackButtonLayoutTitleAction to="/donations" title="Seed Donation Credits">
        <form onSubmit={onSubmit}>
          <Box component={Paper} style={{ padding: 35, marginBottom: 20 }}>
            <DonationsSeedRuleSelector
              control={control}
              setValue={setValue}
              selectedRuleId={donationCreditRuleId}
            />
            <Typography variant="h6" gutterBottom style={{ marginTop: 15 }}>
              Upload Seed File (CSV)
            </Typography>
            <Controller
              name="importedUrl"
              control={control}
              rules={{ required: 'Please upload your seed file' }}
              render={({ field: { onChange } }) => (
                <FilesUpload
                  s3Acl="private"
                  s3Bucket="secure"
                  acceptedTypes={['text/csv']}
                  objectPrefix="donationCreditSeed/raw"
                  onChange={(files, uploadInProgress) => {
                    const publicFileUrl = files?.[0]?.publicUrl;
                    if (uploadInProgress) {
                      setIsUploadingFile(true);
                    }
                    if (!uploadInProgress) {
                      onChange(publicFileUrl);
                      setIsUploadingFile(false);
                    }
                  }}
                  multiple={false}
                />
              )}
            />
            <Controller
              name="note"
              control={control}
              rules={{ required: 'Please enter a note', maxLength: 300 }}
              defaultValue=""
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Note"
                  helperText="Please describe what the seed is for"
                  fullWidth
                  required
                  variant="standard"
                  error={Boolean(errors.note)}
                  style={{ marginTop: 15 }}
                />
              )}
            />
            <div style={{ marginTop: 10 }}>
              <Controller
                control={control}
                name="notifyUser"
                defaultValue={false}
                render={({ field }) => (
                  <Tooltip title="Verify the 'Donation credit received' notification setting">
                    <FormControlLabel
                      control={
                        <Checkbox color="primary" checked={field.value} onChange={field.onChange} />
                      }
                      label={<>Notify users with credit seeding email</>}
                    />
                  </Tooltip>
                )}
              />
            </div>
            <div style={{ marginTop: 10 }}>
              <Controller
                control={control}
                name="createUsers"
                defaultValue={false}
                render={({ field }) => (
                  <Tooltip title="Make sure to run the file first before using this option to check the failures due to missing users match your expectations to avoid creating incorrect/duplicate users due to mispelled or outdated emails">
                    <FormControlLabel
                      control={
                        <Checkbox color="primary" checked={field.value} onChange={field.onChange} />
                      }
                      label={
                        <>
                          Create non-existing users <WarningIcon />
                        </>
                      }
                    />
                  </Tooltip>
                )}
              />
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              style={{ textAlign: 'right', marginTop: 15 }}
              disabled={Boolean(
                !isValid ||
                  isSubmitting ||
                  isUploadingFile ||
                  isLoading ||
                  loadingCreateDonationCreditSeedReceiptMutation
              )}
            >
              {isSubmitting ? <CircularProgress size={22} /> : 'Submit'}
            </Button>
          </Box>
        </form>

        <RecentReceiptsList
          receipts={donationCreditReceiptData?.receipts ?? []}
          loading={donationCreditReceiptLoading}
        />
      </BackButtonLayoutTitleAction>
    </>
  );
};

export default DonationsSeedPage;
